'use client';

import { ModalId } from '@heidi/schema/ml-scribe';
import Image from 'next/image';
import { useEffect, useState, type ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';
import sessionMergeIllustration from '../../assets/session-merge-illustration.jpg';
import { useSessionMergeOnboardingUiHost } from '../../host';

/**
 * The tab is only in the DOM when the open session has a merge group, so
 * anchoring to it enforces the "first session with a Session Merge tab" timing
 * without a separate flag. Both session-detail layouts render the tab:
 * v1 (`ScribeTabs/TabTriggerButton`) as `session-tab-session_merge`, v2
 * (`v2-artifact-tabs`) as `v2-artifact-tab-session_merge` — match either.
 */
const SESSION_MERGE_TAB_ANCHOR_SELECTOR =
  '[data-testid="session-tab-session_merge"], [data-testid="v2-artifact-tab-session_merge"]';

/**
 * Two-step coachmark tour introducing the Session Merge tab (Figma 8071-53612).
 * Composes the shared {@link CoachMark}; visibility is owned by the modal queue
 * (`ModalId.SESSION_MERGE_TAB_COACHMARK`), so it shows at most once per user.
 * Step 1 ("Next") explains auto-merge; step 2 ("Done") gives the best-results
 * tip. The X or Escape closes from either step; all close paths mark the modal
 * seen via `handleClose`.
 */
export function SessionMergeTabCoachmark(): ReactElement | null {
  const { useQueuedModal, CoachMark, SessionMergeIcon } =
    useSessionMergeOnboardingUiHost();
  const { isOpen, handleClose } = useQueuedModal(ModalId.SESSION_MERGE_TAB_COACHMARK);
  const [isLastStep, setIsLastStep] = useState(false);

  // Reset to the first step whenever the coachmark (re-)opens — the queue can
  // re-surface it after it yields to a higher-priority modal.
  useEffect(() => {
    if (isOpen) setIsLastStep(false);
  }, [isOpen]);

  // Mounting only while eligible keeps the CoachMark's anchor observer off until
  // the queue selects this modal.
  if (!isOpen) return null;

  return (
    <CoachMark
      anchorSelector={SESSION_MERGE_TAB_ANCHOR_SELECTOR}
      side="bottom"
      align="start"
      icon={<SessionMergeIcon />}
      title={<FormattedMessage defaultMessage="Session Merge" id="nbo+iJi1NB" />}
      image={
        <Image
          src={sessionMergeIllustration}
          alt=""
          aria-hidden
          fill
          className="object-cover"
        />
      }
      description={
        isLastStep ? (
          <FormattedMessage
            defaultMessage="For best results, mention the patient's full name and date of birth at the start of the session."
            id="HghKzFbdod"
          />
        ) : (
          <FormattedMessage
            defaultMessage="Heidi automatically merges sessions linked to the same patient, creating a more complete note."
            id="vp/f6sRDFF"
          />
        )
      }
      progress={isLastStep ? '2/2' : '1/2'}
      primaryLabel={
        isLastStep ? (
          <FormattedMessage defaultMessage="Done" id="JXdbo8Vnlw" />
        ) : (
          <FormattedMessage defaultMessage="Next" id="9+DdtuCqLw" />
        )
      }
      onPrimary={isLastStep ? handleClose : () => setIsLastStep(true)}
      onDismiss={handleClose}
      data-testid="session-merge-tab-coachmark"
    />
  );
}
